import { motion } from "framer-motion";

import { Section, SectionHead, Card } from "../components/ui";
import { Reveal } from "../components/Reveal";
import { site } from "../data/site";

/**
 * International positioning, drawn as a timezone band rather than a world map.
 *
 * A dotted world map with glowing pins is the single most common visual on
 * offshore development sites, and it says nothing — pins mark ambition as
 * easily as they mark customers. A UTC axis says something checkable: an
 * India engineering day overlaps a Gulf working day almost completely and
 * a UK morning substantially, which is the actual argument for where we
 * are.
 *
 * The two tiers are kept visually distinct on purpose. Filled markers are
 * markets where our own products run in production. Hollow markers are
 * markets the engineering is built for — language, compliance, residency.
 * Collapsing the two would turn a capability claim into a customer claim.
 */

const MIN_UTC = -6;
const MAX_UTC = 9;
const SPAN = MAX_UTC - MIN_UTC;

function pct(utc: number) {
  return ((utc - MIN_UTC) / SPAN) * 100;
}

export function GlobalReach() {
  const markets = [...site.servedMarkets].sort((a, b) => a.utc - b.utc);

  return (
    <Section id="global">
      <div className="container">
        <SectionHead
          eyebrow="Where we work"
          title={
            <>
              Built in India.{" "}
              <span className="text-gradient">Designed for the world.</span>
            </>
          }
          lede="Our engineering day covers a Gulf working day almost end to end and reaches a UK morning. That is the reason to be here — not the rate card."
        />

        <Reveal delay={0.1} className="mt-10 sm:mt-14">
          <Card className="p-6 sm:p-10">
            {/* Band — desktop */}
            <div className="hidden md:block">
              <div className="relative h-32">
                {/* Axis */}
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                  style={{ transformOrigin: "left" }}
                  className="absolute left-0 top-1/2 h-px w-full bg-gradient-to-r from-violet-500/50 via-emerald-400/70 to-teal-400/50"
                />

                {/* Hour ticks */}
                {Array.from({ length: SPAN + 1 }, (_, i) => MIN_UTC + i).map((h) => (
                  <span
                    key={h}
                    style={{ left: `${pct(h)}%` }}
                    className="absolute top-1/2 h-1.5 w-px -translate-x-1/2 bg-white/15"
                  />
                ))}

                {markets.map((m, i) => {
                  const live = m.production;
                  const above = i % 2 === 0;
                  return (
                    <motion.div
                      key={m.code}
                      initial={{ opacity: 0, y: above ? 8 : -8 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.4, delay: 0.35 + i * 0.08 }}
                      style={{ left: `${pct(m.utc)}%` }}
                      className={`absolute -translate-x-1/2 ${
                        above ? "bottom-1/2 pb-4" : "top-1/2 pt-4"
                      }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        {above ? null : (
                          <span
                            className={`h-4 w-px ${live ? "bg-emerald-400/60" : "bg-white/20"}`}
                          />
                        )}
                        <span className="whitespace-nowrap text-xs font-medium text-white/80">
                          {m.name}
                        </span>
                        <span className="font-mono text-[10px] tabular-nums text-white/35">
                          UTC{m.utc >= 0 ? "+" : ""}
                          {m.utc}
                        </span>
                        {above ? (
                          <span
                            className={`h-4 w-px ${live ? "bg-emerald-400/60" : "bg-white/20"}`}
                          />
                        ) : null}
                      </div>
                      <span
                        className={`absolute left-1/2 -translate-x-1/2 rounded-full ${
                          above ? "-bottom-[3px]" : "-top-[3px]"
                        } ${
                          live
                            ? "size-[9px] bg-emerald-400 shadow-[0_0_12px_rgba(16,185,129,0.8)]"
                            : "size-[7px] border border-white/40 bg-ink-950"
                        }`}
                      />
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Band — mobile falls back to a list, because a 15-hour axis on a
             *  phone is unreadable at any font size. */}
            <ul className="grid gap-2.5 md:hidden">
              {markets.map((m) => {
                const live = m.production;
                return (
                  <li
                    key={m.code}
                    className="flex items-center justify-between gap-3 rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2.5"
                  >
                    <span className="flex items-center gap-2.5">
                      <span
                        className={`rounded-full ${
                          live
                            ? "size-2 bg-emerald-400"
                            : "size-2 border border-white/40 bg-transparent"
                        }`}
                      />
                      <span className="text-sm text-white/85">{m.name}</span>
                    </span>
                    <span className="font-mono text-[11px] tabular-nums text-white/35">
                      UTC{m.utc >= 0 ? "+" : ""}
                      {m.utc}
                    </span>
                  </li>
                );
              })}
            </ul>

            {/* Legend — the honesty of this whole section lives here. */}
            <div className="mt-8 flex flex-col gap-3 border-t border-white/[0.08] pt-6 sm:flex-row sm:items-start sm:gap-8">
              <div className="flex items-start gap-2.5">
                <span className="mt-1 size-2 shrink-0 rounded-full bg-emerald-400" />
                <p className="text-xs leading-relaxed text-white/60">
                  <span className="font-semibold text-white/80">In production.</span>{" "}
                  Our own products serve customers here today.
                </p>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="mt-1 size-2 shrink-0 rounded-full border border-white/40" />
                <p className="text-xs leading-relaxed text-white/60">
                  <span className="font-semibold text-white/80">Built for.</span>{" "}
                  Language, compliance and data-residency work already done —
                  not a claim about customers.
                </p>
              </div>
            </div>
          </Card>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {[
              {
                t: "Arabic is not a translation layer",
                b: "Right-to-left through the whole interface, Hijri-aware reporting, and Gulf labour rules in the overtime engine rather than bolted on top of generic defaults.",
              },
              {
                t: "Eight languages, matched not translated",
                b: "Devanagari, Roman-script Hinglish, Tamil, Telugu, Gujarati, Marathi, Arabic and English — the agent holds the buyer's register across the whole conversation.",
              },
              {
                t: "Residency is a configuration",
                b: "Where the data lives, who may read it and how long it is kept are deployment decisions, because in the GCC and India they have to be.",
              },
            ].map((x) => (
              <div
                key={x.t}
                className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-5"
              >
                <h3 className="text-sm font-semibold text-white">{x.t}</h3>
                <p className="mt-2 text-xs leading-relaxed text-white/55">{x.b}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </Section>
  );
}
